// Référencement (SEO) : titre, description et adresse canonique de chaque
// page publique, mis à jour à chaque changement de page. Les pages privées
// (compte, paiement, admin...) sont marquées « noindex » pour que Google ne
// les affiche pas dans ses résultats.
import { useEffect } from "react";

export const SITE_URL = "https://vanta-pulse.shop";
const SITE_NAME = "VantaPulse";
const DEFAULT_TITLE = "VantaPulse – Scripts FiveM, anti-cheat et bots Discord";
const DEFAULT_DESCRIPTION =
  "Scripts FiveM (téléphone et tablette VantaPhone pour ESX, QBCore et ox_core), anti-cheat VantaGuard avec écran du joueur en direct, bots Discord en français et Vanta AI, l'assistant IA qui code pour toi.";

const VANTA_AI_DESCRIPTION =
  "Vanta AI, l'assistant IA de VantaPulse : une intelligence artificielle en français qui écrit tes scripts FiveM et Roblox, tes bots Discord et tes sites (aperçu en direct, projets .zip), crée des modèles 3D et cherche sur le web. Gratuit pour commencer.";

// Pages produit (/bot/:id).
const PRODUCTS = {
  gestion: {
    title: "Bot Discord de gestion et modération – Vanta Pulse Gestion",
    description:
      "Bot Discord de gestion tout-en-un en français : modération, tickets avec prise en charge, anti-raid, anti-lien, anti-spam, mots bannis, giveaways, messages de bienvenue avec bannière et suivi des invitations.",
  },
  radio: {
    title: "Bot Discord musique et radio – Vanta Pulse Radio",
    description:
      "Bot Discord musique et radio : titres, playlists, file d’attente, filtres et volume, et radios en continu dans tes salons vocaux. Ton propre bot avec ton nom et ton logo.",
  },
  vantaguard: {
    title: "Anti-cheat FiveM VantaGuard – écran en direct, bans et webhooks | VantaPulse",
    description:
      "Anti-cheat FiveM réglable depuis le site : 35 détections, écran du joueur en direct, captures, bans par identifiants et matériel, vagues de bans, équipe de staff et webhooks Discord.",
  },
  "vantaphone-yphone": {
    title: "Téléphone FiveM Yphone 18 Pro – Script VantaPhone (ESX, QBCore)",
    description:
      "Script téléphone FiveM complet : appels, SMS, WhatsUp, Vistagram, TokTik, Ghosty, Spotifi, appareil photo et vidéo, Maps, banque, services. Compatible ESX, QBCore, ox_core et standalone.",
  },
  "vantaphone-tablette": {
    title: "Tablette FiveM pour les métiers – Script VantaPhone Tpadlette",
    description:
      "Tablette FiveM des métiers : espace pro, prise de service, messagerie pro, terminal police et EMS (MDT), apps métier personnalisables. Compatible ESX, QBCore et ox_core.",
  },
  "vantaphone-pack": {
    title: "Pack téléphone + tablette FiveM – VantaPhone",
    description:
      "Le pack FiveM complet : le téléphone Yphone 18 Pro et la tablette Tpadlette avec une seule licence. Compatible ESX, QBCore, ox_core et standalone.",
  },
};

// Pages publiques (indexées).
const PAGES = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  "/shop": {
    title: "Boutique – Acheter un bot Discord ou un script FiveM | VantaPulse",
    description:
      "Achète ton bot Discord personnalisé (gestion, radio) ou ton script FiveM (téléphone, tablette). Abonnement ou à vie, activation rapide, support en français.",
  },
  "/comparer": {
    title: "Comparer les bots Discord et scripts FiveM | VantaPulse",
    description: "Compare les fonctionnalités et les prix des bots Discord et des scripts FiveM VantaPulse pour choisir l’offre adaptée à ton serveur.",
  },
  "/support": {
    title: "Support et aide | VantaPulse",
    description: "Besoin d’aide avec ton bot Discord ou ton script FiveM VantaPulse ? Contacte le support en français.",
  },
  "/tutorial": {
    title: "Tutoriel : installer et configurer ton bot Discord | VantaPulse",
    description: "Guide pas à pas pour créer, installer et configurer ton bot Discord VantaPulse sur ton serveur.",
  },
  "/status": {
    title: "Statut des services | VantaPulse",
    description: "État en temps réel des bots Discord et des services VantaPulse.",
  },
  "/mentions-legales": { title: "Mentions légales | VantaPulse", description: "Mentions légales du site VantaPulse." },
  "/cgv": { title: "Conditions générales de vente | VantaPulse", description: "Conditions générales de vente des produits VantaPulse." },
  "/remboursement": { title: "Politique de remboursement | VantaPulse", description: "Politique de remboursement des produits VantaPulse." },
  "/politique-confidentialite": { title: "Politique de confidentialité | VantaPulse", description: "Comment VantaPulse protège tes données personnelles." },
  "/conditions-utilisation": { title: "Conditions d’utilisation | VantaPulse", description: "Conditions d’utilisation du site et des services VantaPulse." },
};

function seoFor(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (PAGES[path]) return { ...PAGES[path], path, index: true };
  const m = path.match(/^\/bot\/([\w-]+)$/);
  if (m) {
    const id = m[1] === "vantaphone-tphone" || m[1] === "vantaphone-sphone" ? "vantaphone-yphone" : m[1];
    const p = PRODUCTS[id];
    if (p) return { ...p, path: `/bot/${id}`, index: true };
    return { title: `${SITE_NAME} – Produit`, description: DEFAULT_DESCRIPTION, path, index: true };
  }
  // Vanta AI : la page publique est référencée, les conversations non ; l'onglet dit « Vanta AI ».
  if (path === "/ai") return { title: `${SITE_NAME} – AI`, description: VANTA_AI_DESCRIPTION, path, index: true };
  if (path.startsWith("/ai/")) return { title: `${SITE_NAME} – AI`, description: VANTA_AI_DESCRIPTION, path, index: false };
  // Compte, paiement, admin, connexion, page introuvable : pas dans Google.
  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, path, index: false };
}

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo(pathname) {
  useEffect(() => {
    const s = seoFor(pathname || "/");
    const url = `${SITE_URL}${s.path === "/" ? "/" : s.path}`;
    document.title = s.title;
    setMeta("name", "description", s.description);
    setMeta("name", "robots", s.index ? "index, follow, max-image-preview:large" : "noindex, nofollow");
    setMeta("property", "og:title", s.title);
    setMeta("property", "og:description", s.description);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", s.title);
    setMeta("name", "twitter:description", s.description);
    setCanonical(url);
  }, [pathname]);
}
